import { useState } from 'react';
import { Box, Stack, Group, Avatar, Badge, TextInput, ScrollArea, ActionIcon, Indicator } from '@mantine/core';
import {
  IconSearch,
  IconSend,
  IconPaperclip,
  IconMoodSmile,
  IconPhone,
  IconVideo,
  IconDots,
  IconPin,
  IconBellOff,
  IconUsers,
} from '@tabler/icons-react';
import { Card, Text, Title } from '../../../../components/ui';
import { mockConversations, mockMessages, Conversation, Message } from '../mockData/chat';

const formatTime = (date: Date) => {
  const now = new Date();
  const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays === 0) {
    return new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit' }).format(date);
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return new Intl.DateTimeFormat('en-US', { weekday: 'short' }).format(date);
  }
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(date);
};

const ConversationItem = ({
  conversation,
  isActive,
  onClick
}: {
  conversation: Conversation;
  isActive: boolean;
  onClick: () => void;
}) => {
  const displayName = conversation.type === 'group'
    ? conversation.name
    : conversation.participantNames.find(n => n !== 'John Doe');

  return (
    <Box
      p="sm"
      onClick={onClick}
      style={{
        backgroundColor: isActive ? 'var(--mantine-color-blue-light)' : undefined,
        borderRadius: 8,
        cursor: 'pointer',
      }}
    >
      <Group gap="sm" justify="space-between">
        <Group gap="sm" style={{ flex: 1 }}>
          <Indicator
            color={conversation.isOnline ? 'green' : 'gray'}
            position="bottom-end"
            size={10}
            offset={4}
            disabled={conversation.type === 'group'}
          >
            <Avatar size="md" radius="xl" color={conversation.type === 'group' ? 'blue' : 'cyan'}>
              {conversation.type === 'group' ? (
                <IconUsers size={16} />
              ) : (
                displayName?.split(' ').map(n => n[0]).join('')
              )}
            </Avatar>
          </Indicator>
          <Box style={{ flex: 1 }}>
            <Group gap="xs" justify="space-between">
              <Group gap={4}>
                {conversation.isPinned && <IconPin size={12} color="var(--mantine-color-dimmed)" />}
                <Text size="sm" fw={conversation.unreadCount > 0 ? 700 : 500}>{displayName}</Text>
              </Group>
              <Text size="xs" c="dimmed">{formatTime(conversation.lastMessageAt)}</Text>
            </Group>
            <Group gap="xs" justify="space-between">
              <Text size="xs" c="dimmed" lineClamp={1} style={{ flex: 1 }}>
                {conversation.lastMessage}
              </Text>
              <Group gap={4}>
                {conversation.isMuted && <IconBellOff size={12} color="var(--mantine-color-dimmed)" />}
                {conversation.unreadCount > 0 && (
                  <Badge size="xs" circle>{conversation.unreadCount}</Badge>
                )}
              </Group>
            </Group>
          </Box>
        </Group>
      </Group>
    </Box>
  );
};

const MessageBubble = ({ message, isOwn }: { message: Message; isOwn: boolean }) => (
  <Box
    style={{
      display: 'flex',
      justifyContent: isOwn ? 'flex-end' : 'flex-start',
      marginBottom: 8,
    }}
  >
    <Box
      p="sm"
      style={{
        backgroundColor: isOwn ? 'var(--mantine-color-blue-filled)' : 'var(--mantine-color-gray-light)',
        borderRadius: 12,
        maxWidth: '70%',
      }}
    >
      {!isOwn && (
        <Text size="xs" c={isOwn ? 'white' : 'blue'} fw={500} mb={4}>
          {message.senderName}
        </Text>
      )}
      <Text size="sm" c={isOwn ? 'white' : undefined}>
        {message.content}
      </Text>
      {message.attachments && message.attachments.length > 0 && (
        <Box mt="xs" p="xs" style={{ backgroundColor: 'rgba(0,0,0,0.1)', borderRadius: 8 }}>
          {message.attachments.map(att => (
            <Text key={att.id} size="xs" c={isOwn ? 'white' : 'dimmed'}>
              📎 {att.name}
            </Text>
          ))}
        </Box>
      )}
      {message.reactions && message.reactions.length > 0 && (
        <Group gap={4} mt="xs">
          {message.reactions.map(reaction => (
            <Badge key={reaction.emoji} size="xs" variant="light" color="gray">
              {reaction.emoji} {reaction.userIds.length}
            </Badge>
          ))}
        </Group>
      )}
      <Text size="xs" c={isOwn ? 'rgba(255,255,255,0.7)' : 'dimmed'} ta="right" mt={4}>
        {formatTime(message.createdAt)}
      </Text>
    </Box>
  </Box>
);

export default function ChatPage() {
  const [activeConversation, setActiveConversation] = useState<string>(mockConversations[0].id);
  const [search, setSearch] = useState('');
  const [newMessage, setNewMessage] = useState('');

  const currentConversation = mockConversations.find(c => c.id === activeConversation);
  const conversationMessages = mockMessages.filter(m => m.conversationId === activeConversation);

  const filteredConversations = mockConversations.filter(conv => {
    const name = conv.type === 'group' ? conv.name : conv.participantNames.find(n => n !== 'John Doe');
    return name?.toLowerCase().includes(search.toLowerCase());
  });

  // Sort: pinned first, then by last message time
  const sortedConversations = [...filteredConversations].sort((a, b) => {
    if (a.isPinned && !b.isPinned) return -1;
    if (!a.isPinned && b.isPinned) return 1;
    return b.lastMessageAt.getTime() - a.lastMessageAt.getTime();
  });

  const displayName = currentConversation?.type === 'group'
    ? currentConversation.name
    : currentConversation?.participantNames.find(n => n !== 'John Doe');

  return (
    <Group gap={0} style={{ height: 'calc(100vh - 200px)', minHeight: 500 }} align="stretch">
      {/* Conversations List */}
      <Card style={{ width: 320, borderRadius: '12px 0 0 12px' }} p={0}>
        <Stack gap={0} h="100%">
          <Box p="md" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
            <Title order={4} mb="sm">Messages</Title>
            <TextInput
              placeholder="Search conversations..."
              leftSection={<IconSearch size={16} />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Box>
          <ScrollArea style={{ flex: 1 }} p="xs">
            <Stack gap={4}>
              {sortedConversations.map(conv => (
                <ConversationItem
                  key={conv.id}
                  conversation={conv}
                  isActive={conv.id === activeConversation}
                  onClick={() => setActiveConversation(conv.id)}
                />
              ))}
            </Stack>
          </ScrollArea>
        </Stack>
      </Card>

      {/* Chat Area */}
      <Card style={{ flex: 1, borderRadius: '0 12px 12px 0', borderLeft: 'none' }} p={0}>
        <Stack gap={0} h="100%">
          {/* Chat Header */}
          <Group justify="space-between" p="md" style={{ borderBottom: '1px solid var(--mantine-color-gray-3)' }}>
            <Group gap="sm">
              <Indicator
                color={currentConversation?.isOnline ? 'green' : 'gray'}
                position="bottom-end"
                size={10}
                offset={4}
                disabled={currentConversation?.type === 'group'}
              >
                <Avatar size="md" radius="xl" color={currentConversation?.type === 'group' ? 'blue' : 'cyan'}>
                  {currentConversation?.type === 'group' ? (
                    <IconUsers size={16} />
                  ) : (
                    displayName?.split(' ').map(n => n[0]).join('')
                  )}
                </Avatar>
              </Indicator>
              <Box>
                <Text fw={600}>{displayName}</Text>
                <Text size="xs" c="dimmed">
                  {currentConversation?.type === 'group'
                    ? `${currentConversation.participants.length} members`
                    : currentConversation?.isOnline ? 'Online' : 'Offline'}
                </Text>
              </Box>
            </Group>
            <Group gap="xs">
              <ActionIcon variant="light" size="lg">
                <IconPhone size={18} />
              </ActionIcon>
              <ActionIcon variant="light" size="lg">
                <IconVideo size={18} />
              </ActionIcon>
              <ActionIcon variant="light" size="lg">
                <IconDots size={18} />
              </ActionIcon>
            </Group>
          </Group>

          {/* Messages */}
          <ScrollArea style={{ flex: 1 }} p="md">
            {conversationMessages.map(message => (
              <MessageBubble
                key={message.id}
                message={message}
                isOwn={message.senderId === 'u1'}
              />
            ))}
          </ScrollArea>

          {/* Message Input */}
          <Box p="md" style={{ borderTop: '1px solid var(--mantine-color-gray-3)' }}>
            <Group gap="sm">
              <ActionIcon variant="light" size="lg">
                <IconPaperclip size={18} />
              </ActionIcon>
              <TextInput
                placeholder="Type a message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                style={{ flex: 1 }}
              />
              <ActionIcon variant="light" size="lg">
                <IconMoodSmile size={18} />
              </ActionIcon>
              <ActionIcon variant="filled" size="lg">
                <IconSend size={18} />
              </ActionIcon>
            </Group>
          </Box>
        </Stack>
      </Card>
    </Group>
  );
}
